import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import {
  Container,
  Header,
  HeaderLogo,
  Content,
  SubHeader,
  AnalysisTitle,
  FormSection,
  FooterButtons,
  BackButton,
  SubmitButton,
  LoadingContainer,
  LoadingText,
  ProgressTrack,
  ProgressBar,
  ResultCard,
  CardTopRow,
  InsurerLogo,
  InsurerInfo,
  InsurerName,
  DetailsRow,
  DetailColumn,
  DetailLabel,
  DetailValue,
  ActionButtonsRow,
  FinalBackButton,
  ValidationContainer,
  ValidateTitle,
  ValidationSectionTitle,
  InputGroup,
  Row,
  StyledInput,
  ToggleInputContainer,
  StatusBadge,
  StatusDot,
  StatusText,
  Divider,
  RadioGroup,
  RadioButton
} from '../styles';
import { FaChevronLeft, FaPencilAlt, FaCheck } from 'react-icons/fa';
import logorv from '../../../resources/imgs/logo-lado-bom-seguros.svg';
import Input from '../../../components/Input';
import { FieldLabel } from '../../../styles';
import { Dropdown } from 'semantic-ui-react';
import { warningNotification, successNotification } from '../../../services/notification';
import { unmaskCpfOrCnpj, numberToMoneyString } from '../../../services/masks';
import { get, post } from '../../../services/request';
import moment from 'moment-timezone';

import {
  getOccupationTypeOptions,
  getPropertyTypeOptions
} from '../../fire-insurance/budget/forms-based-in-insurer/Alfa/constants/form.constant';

interface AlfaBudgetFormProps {
  estate: any;
  insurerId: string;
  userId: string;
  percentage: number;
  onBudgetCreated?: (budgetId: string) => void;
  onBack: () => void;
}

type AnalysisStep = 'LOADING' | 'FORM' | 'RESULT';

const PwaAlfaBudgetForm: React.FC<AlfaBudgetFormProps> = ({
  estate,
  insurerId,
  userId,
  percentage,
  onBudgetCreated,
  onBack
}) => {
  const [step, setStep] = useState<AnalysisStep>('LOADING');
  const [loadingMsg, setLoadingMsg] = useState('Preparando sua cotação Alfa...');
  const [channels, setChannels] = useState<any[]>([]);
  const [plans, setPlans] = useState<any[]>([]);
  const [occupationOptions, setOccupationOptions] = useState<any[]>([]);
  const [propertyTypeOptions, setPropertyTypeOptions] = useState<any[]>([]);
  const [coverages, setCoverages] = useState<any[]>([]);
  const [installmentsOptions, setInstallmentsOptions] = useState<any[]>([]);
  const [initialValues, setInitialValues] = useState<any>(null);
  const [resultData, setResultData] = useState<any>(null);

  const loadPlans = useCallback(async () => {
    try {
      const plansByEstate: any[] = await get(`/alfa-seguros/plan-estate/${estate.id}`);
      const plansList: any[] = [];
      const channelsList: any[] = [];
      const channelIds: string[] = [];

      plansByEstate.forEach(curr => {
        plansList.push({
          key: curr.plans.code,
          text: curr.plans.name,
          value: {
            code: curr.plans.code,
            modality: curr.plans.channel.modality,
            id: curr.plans.id
          }
        });

        if (!channelIds.includes(curr.plans.channel.id)) {
          channelIds.push(curr.plans.channel.id);
          channelsList.push({
            key: curr.plans.channel.id,
            text: curr.plans.channel.name,
            value: {
              id: curr.plans.channel.id,
              modality: curr.plans.channel.modality,
              code: curr.plans.channel.code,
              name: curr.plans.channel.name
            }
          });
        }
      });
      setPlans(plansList);
      setChannels(channelsList);
      return { plans: plansList, channels: channelsList };
    } catch (e) {
      warningNotification('Ops', 'Erro ao carregar planos.');
      return { plans: [], channels: [] };
    }
  }, [estate.id]);

  const loadCoverages = useCallback(async () => {
    try {
      const cvgs = await get(`/alfa-seguros/coverage/coverage-estate/${estate.id}?insurerId=${insurerId}`);
      setCoverages(cvgs);
      return cvgs;
    } catch (e) {
      console.error(e);
      return [];
    }
  }, [estate.id, insurerId]);

  const loadConfigs = useCallback(async () => {
    try {
      const configs = await get(`/estates/fire-active-insurer/${estate.id}/${insurerId}`);
      if (configs.installmentOptions) {
        const options = configs.installmentOptions.split(',').map((option: any) => ({
          key: option,
          text: `${option}x`,
          value: option
        }));
        setInstallmentsOptions(options);
      }
    } catch (e) {
      console.error(e);
    }
  }, [estate.id, insurerId]);

  const initializeData = useCallback(async () => {
    setLoadingMsg('Carregando informações...');
    const [{ channels: loadedChannels, plans: loadedPlans }, loadedCoverages] = await Promise.all([loadPlans(), loadCoverages(), loadConfigs()]);

    // Setup initial form values
    let estateNumber = estate.estateNumber ? estate.estateNumber.replace(/[^0-9]/g, '') : '0';
    if (!estateNumber) estateNumber = '0';

    const initialChan = loadedChannels.length === 1 ? loadedChannels[0].value : null;

    let initialProp = '';
    let initialOcc = '';
    let initialPlan = '';
    let initialEndVigency = moment().add(1, 'year').format('YYYY-MM-DD');
    let initialCvgs: any[] = [];

    if (initialChan) {
      const pType = getPropertyTypeOptions(initialChan.modality);
      const oType = getOccupationTypeOptions(initialChan.modality);
      if (pType) {
        setPropertyTypeOptions(pType);
        if (pType.length === 1) initialProp = pType[0].value;
      }
      if (oType) {
        setOccupationOptions(oType);
        if (oType.length === 1) initialOcc = oType[0].value;
      }

      const filteredPlans = loadedPlans.filter((p: any) => p.text.includes(initialChan.name));
      if (filteredPlans.length === 1) {
        initialPlan = filteredPlans[0].value;
      }

      const startDate = new Date();
      initialEndVigency = initialChan.name.includes('Anual')
        ? moment(startDate).add(1, 'year').format('YYYY-MM-DD')
        : moment(startDate).add(1, 'month').format('YYYY-MM-DD');

      initialCvgs = (loadedCoverages || [])
        .filter((c: any) => c.coverage?.channel?.code === initialChan.code)
        .map((c: any) => ({
          id: c.id,
          code: c.coverage?.code,
          checked: c.isRequired || c.coverage?.isDefault,
          value: 0,
          name: c.coverage?.name,
          onEdit: false,
          onEditValue: 0,
          maxValue: c.maxValue,
          minValue: c.minValue,
          multiplier: c.multiplier,
          isDefault: c.coverage?.isDefault,
          isRequired: c.isRequired,
          maxMultiplier: c.coverage?.maxMultiplier || c.maxMultiplier
        }))
        .sort((a: any, b: any) => {
          if (a.isRequired && !b.isRequired) return -1;
          if (!a.isRequired && b.isRequired) return 1;
          if (a.name.toLowerCase().includes('perda de aluguel') && !b.name.toLowerCase().includes('perda de aluguel')) return -1;
          if (!a.name.toLowerCase().includes('perda de aluguel') && b.name.toLowerCase().includes('perda de aluguel')) return 1;
          return 0;
        });
    }

    setInitialValues({
      channel: initialChan,
      propertyType: initialProp,
      occupationType: initialOcc,
      coverageType: '4', // Default
      startVigency: moment().format('YYYY-MM-DD'),
      endVigency: initialEndVigency,
      plans: initialPlan,
      rentValue: '',
      installments: '1',
      action: 0,
      tenantName: estate.name,
      tenantDocument: estate.cnpj,
      propertyOwnerName: '',
      propertyOwnerDocument: '',
      zipCode: estate.estateZipCode,
      street: estate.estateStreet,
      district: estate.estateDistrict,
      city: estate.estateCity,
      state: estate.estateState,
      number: estateNumber,
      complement: estate.estateComplement || '',
      coverages: initialCvgs
    });

    setStep('FORM');
  }, [estate, loadPlans, loadCoverages, loadConfigs]);

  useEffect(() => {
    initializeData();
  }, [initializeData]);

  const handleChangeChannel = (channel: any, setFieldValue: any) => {
    if (!channel) {
      setOccupationOptions([]);
      setPropertyTypeOptions([]);
      setFieldValue('propertyType', '');
      setFieldValue('occupationType', '');
      setFieldValue('plans', '');
      return;
    }
    const pType = getPropertyTypeOptions(channel.modality);
    const oType = getOccupationTypeOptions(channel.modality);
    if (pType) {
      setPropertyTypeOptions(pType);
      if (pType.length === 1) setFieldValue('propertyType', pType[0].value);
    }
    if (oType) {
      setOccupationOptions(oType);
      if (oType.length === 1) setFieldValue('occupationType', oType[0].value);
    }

    // Auto-select plan if only one matches the modality
    const filteredPlans = plans.filter((p: any) => p.text.includes(channel.name));
    if (filteredPlans.length === 1) {
      setFieldValue('plans', filteredPlans[0].value);
    }

    // Auto-select end vigency based on channel name
    const startDate = new Date();
    const endDate = channel.name.includes('Anual')
      ? moment(startDate).add(1, 'year').format('YYYY-MM-DD')
      : moment(startDate).add(1, 'month').format('YYYY-MM-DD');
    setFieldValue('endVigency', endDate);

    // Filter Coverages
    const filteredCvgs = coverages
      .filter(c => c.coverage?.channel?.code === channel.code)
      .map(c => ({
        id: c.id,
        code: c.coverage?.code,
        checked: c.isRequired || c.coverage?.isDefault,
        value: 0,
        name: c.coverage?.name,
        onEdit: false,
        onEditValue: 0,
        maxValue: c.maxValue,
        minValue: c.minValue,
        multiplier: c.multiplier,
        isDefault: c.coverage?.isDefault,
        isRequired: c.isRequired,
        maxMultiplier: c.coverage?.maxMultiplier || c.maxMultiplier
      }))
      .sort((a: any, b: any) => {
        if (a.isRequired && !b.isRequired) return -1;
        if (!a.isRequired && b.isRequired) return 1;
        if (a.name.toLowerCase().includes('perda de aluguel') && !b.name.toLowerCase().includes('perda de aluguel')) return -1;
        if (!a.name.toLowerCase().includes('perda de aluguel') && b.name.toLowerCase().includes('perda de aluguel')) return 1;
        return 0;
      });
    setFieldValue('coverages', filteredCvgs);
  };

  const validationSchema = Yup.object().shape({
    channel: Yup.mixed().required('Obrigatório'),
    propertyType: Yup.string().required('Obrigatório'),
    occupationType: Yup.string().required('Obrigatório'),
    coverageType: Yup.string().required('Obrigatório'),
    startVigency: Yup.string().required('Obrigatório'),
    endVigency: Yup.string().required('Obrigatório'),
    plans: Yup.string().required('Obrigatório'),
    rentValue: Yup.string().required('Obrigatório'),
    installments: Yup.string().required('Obrigatório')
  });

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    setLoadingMsg('Enviando cotação para a Seguradora...');
    setStep('LOADING');
    try {
      const payload = {
        estateId: estate.id,
        insurerId: insurerId,
        isEdit: false,
        isIframe: false,
        data: {
          rentValue: values.rentValue,
          installments: Number(values.installments),
          informedCost: 0,
          action: 0,
          policyPeriodStart: values.startVigency,
          policyPeriodEnd: values.endVigency,
          channel: Number(values.channel.code),
          tenantDocument: unmaskCpfOrCnpj(values.tenantDocument),
          tenantName: values.tenantName,
          discountPercentage: 0,
          tenantSex: '0',
          propertyType: Number(values.propertyType),
          propertyUseType: Number(values.occupationType),
          coverageType: Number(values.coverageType),
          planCode: values.plans.code || values.plans,
          property: {
            zipcode: values.zipCode?.replace(/\D/g, ''),
            city: values.city,
            state: values.state,
            street: values.street,
            district: values.district,
            number: values.number,
            complement: values.complement
          },
          coverages: values.coverages
            .filter((c: any) => c.checked)
            .map((c: any) => {
              let val = c.value;
              if (!val && c.multiplier && values.rentValue) {
                const rv = values.rentValue;
                val = rv * c.multiplier;
              }
              return {
                coverageName: c.name,
                referenceCode: Number(c.code),
                value: Number(val)
              }
            })
        }
      };

      const res = await post('/fire-insurance-quotations/alfa-seguros/budget', payload);

      if (res.status === 200 && !res.data?.message) {
        setResultData({
          maxPremium: res.data.maxPremium,
          fireInsuranceId: res.data.fireInsuranceId,
          ...res.data
        });
        if (onBudgetCreated && res.data.fireInsuranceId) {
          onBudgetCreated(res.data.fireInsuranceId);
        }
        successNotification('Sucesso', 'Orçamento criado com sucesso!');
        setStep('RESULT');
      } else {
        warningNotification('Erro', res.data?.message || 'Falha ao realizar cotação.');
        setStep('FORM');
      }
    } catch (e) {
      console.error(e);
      warningNotification('Ops', 'Ocorreu um erro ao realizar a cotação.');
      setStep('FORM');
    } finally {
      setSubmitting(false);
    }
  };

  if (step === 'LOADING') {
    return (
      <Container>
        <Header>
          <HeaderLogo src={logorv} alt="Logo" />
        </Header>
        <LoadingContainer>
          <LoadingText>{loadingMsg}</LoadingText>
          <ProgressTrack>
            <ProgressBar />
          </ProgressTrack>
        </LoadingContainer>
      </Container>
    );
  }

  if (step === 'RESULT' && resultData) {
    return (
      <Container>
        <Header>
          <button style={{ background: 'none', border: 'none', color: '#59249b', display: 'flex', alignItems: 'center', gap: 4 }} onClick={onBack}>
            <FaChevronLeft /> Voltar
          </button>
          <HeaderLogo src={logorv} alt="Logo" />
        </Header>
        <Content>
          <SubHeader>
            <AnalysisTitle>Resultado da Cotação</AnalysisTitle>
          </SubHeader>
          <ResultCard>
            <CardTopRow>
              <InsurerLogo color="#E5212E">ALFA</InsurerLogo>
              <InsurerInfo>
                <InsurerName>Alfa Seguradora</InsurerName>
                <StatusBadge type="approved">
                  <StatusDot type="approved" />
                  <StatusText>Pronto para Contratação</StatusText>
                </StatusBadge>
              </InsurerInfo>
            </CardTopRow>
            <Divider />
            <DetailsRow>
              <DetailColumn>
                <DetailLabel>Prêmio Total</DetailLabel>
                <DetailValue>{numberToMoneyString(resultData.maxPremium)}</DetailValue>
              </DetailColumn>
              <DetailColumn style={{ alignItems: 'flex-end' }}>
                <DetailLabel>ID do Orçamento</DetailLabel>
                <DetailValue>{resultData.fireInsuranceId?.split('-')[0] || 'N/A'}</DetailValue>
              </DetailColumn>
            </DetailsRow>
          </ResultCard>

          <ActionButtonsRow style={{ marginTop: 'auto' }}>
            <FinalBackButton onClick={onBack}>Novo Orçamento / Voltar</FinalBackButton>
          </ActionButtonsRow>
        </Content>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <button style={{ background: 'none', border: 'none', color: '#59249b', display: 'flex', alignItems: 'center', gap: 4 }} onClick={onBack}>
          <FaChevronLeft /> Voltar
        </button>
        <HeaderLogo src={logorv} alt="Logo" />
      </Header>

      <Content>
        <ValidationContainer>
          <ValidateTitle>
            <span>Alfa Seguradora</span>
          </ValidateTitle>

          {initialValues && (
            <Formik
              enableReinitialize
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ values, errors, touched, handleChange, setFieldValue, submitForm, isSubmitting }) => (
                <FormSection>
                  <ValidationSectionTitle>Dados do Seguro</ValidationSectionTitle>

                  <InputGroup>
                    <FieldLabel>Finalidade da Locação</FieldLabel>
                    <RadioGroup style={{ flexWrap: 'wrap' }}>
                      {channels.map((ch: any) => (
                        <RadioButton
                          key={ch.value.id}
                          active={values.channel?.id === ch.value.id}
                          onClick={() => {
                            setFieldValue('channel', ch.value);
                            handleChangeChannel(ch.value, setFieldValue);
                          }}
                        >
                          <span style={{ fontSize: '14px', width: '100%', textAlign: 'center' }}>
                            {ch.text}
                          </span>
                        </RadioButton>
                      ))}
                    </RadioGroup>
                  </InputGroup>

                  {values.channel && (
                    <>
                      <InputGroup>
                        <FieldLabel>Tipo do Imóvel</FieldLabel>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                          {propertyTypeOptions.map((pt: any) => (
                            <RadioButton
                              key={pt.value}
                              active={values.propertyType === pt.value}
                              onClick={() => setFieldValue('propertyType', pt.value)}
                              style={{ width: '100%', padding: '0 8px' }}
                            >
                              <span style={{ fontSize: '13px', width: '100%', textAlign: 'center' }}>
                                {pt.text}
                              </span>
                            </RadioButton>
                          ))}
                        </div>
                      </InputGroup>

                      <InputGroup>
                        <FieldLabel>Atividade Principal / Ocupação</FieldLabel>
                        <Dropdown
                          fluid selection search
                          options={occupationOptions}
                          value={values.occupationType}
                          onChange={(_, { value }) => setFieldValue('occupationType', value)}
                          placeholder="Busque a ocupação..."
                        />
                      </InputGroup>

                      <InputGroup>
                        <FieldLabel>Assistência</FieldLabel>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          {plans.filter((p: any) => p.text.includes(values.channel.name)).map((plan: any) => (
                            <RadioButton
                              key={plan.value.id}
                              active={values.plans?.id === plan.value.id || values.plans === plan.value}
                              onClick={() => setFieldValue('plans', plan.value)}
                              style={{ width: '100%', justifyContent: 'flex-start' }}
                            >
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{
                                  width: '20px', height: '20px', borderRadius: '50%',
                                  border: `2px solid ${values.plans?.id === plan.value.id || values.plans === plan.value ? '#59249B' : '#DFDFDF'}`,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                                }}>
                                  {(values.plans?.id === plan.value.id || values.plans === plan.value) && (
                                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#59249B' }} />
                                  )}
                                </div>
                                <span style={{ fontSize: '14px', textAlign: 'left' }}>
                                  {plan.text}
                                </span>
                              </div>
                            </RadioButton>
                          ))}
                        </div>
                      </InputGroup>
                    </>
                  )}

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
                    <InputGroup style={{ width: '100%' }}>
                      <Input
                        name="rentValue"
                        label="Valor do Aluguel"
                        placeholder="R$ 0,00"
                        value={values.rentValue ? numberToMoneyString(values.rentValue) : ''}
                        onChange={(e: any) => {
                          const onlyNumber = e.target.value.replace(/\D/g, '');
                          const value = Number(onlyNumber) / 100;
                          setFieldValue('rentValue', value);
                        }}
                        onBlur={() => {
                          values.coverages.forEach((c: any, index: number) => {
                            if (!c.checked) return;
                            const multiplier = c.maxMultiplier
                              ? c.maxMultiplier < c.multiplier
                                ? c.maxMultiplier
                                : c.multiplier
                              : c.multiplier;
                            const val = (values.rentValue || 0) * multiplier;
                            if (val > c.maxValue) {
                              setFieldValue(`coverages[${index}].value`, c.maxValue);
                              setFieldValue(`coverages[${index}].onEditValue`, c.maxValue);
                              return;
                            }
                            if (val < c.minValue) {
                              setFieldValue(`coverages[${index}].value`, c.minValue);
                              setFieldValue(`coverages[${index}].onEditValue`, c.minValue);
                              return;
                            }
                            setFieldValue(`coverages[${index}].value`, val);
                            setFieldValue(`coverages[${index}].onEditValue`, val);
                          });
                        }}
                      />
                    </InputGroup>
                    <InputGroup style={{ width: '100%' }}>
                      <FieldLabel>Parcelas</FieldLabel>
                      <Dropdown
                        fluid selection
                        options={installmentsOptions}
                        value={values.installments}
                        onChange={(_, { value }) => setFieldValue('installments', value)}
                      />
                    </InputGroup>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '16px', background: '#F3F4F6', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase' }}>Período de Vigência</span>
                    <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                      <div style={{ flex: 1 }}>
                        <FieldLabel>Início</FieldLabel>
                        <ToggleInputContainer style={{ background: '#FFFFFF' }}>
                          <StyledInput
                            type="date"
                            disabled
                            value={values.startVigency}
                          // onChange={(e) => setFieldValue('startVigency', e.target.value)}
                          />
                        </ToggleInputContainer>
                      </div>
                      <div style={{ flex: 1 }}>
                        <FieldLabel>Término previsto</FieldLabel>
                        <div style={{ height: '42px', display: 'flex', alignItems: 'center', padding: '0 5px', color: '#4B5563', fontWeight: 600, fontSize: '15px' }}>
                          {moment(values.endVigency).format('DD/MM/YYYY')}
                        </div>
                      </div>
                    </div>
                  </div>

                  <ValidationSectionTitle style={{ marginTop: 16, marginBottom: 8 }}>Coberturas Disponíveis</ValidationSectionTitle>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {values.coverages.map((cov: any, index: number) => (
                      <div key={cov.code} style={{ display: 'flex', flexDirection: 'column', padding: '12px 16px', background: '#F9FAFB', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, paddingRight: '8px' }}>
                            <input
                              type="checkbox"
                              checked={cov.checked}
                              disabled={cov.isRequired}
                              style={{ width: '18px', height: '18px', accentColor: '#59249B', cursor: cov.isRequired ? 'not-allowed' : 'pointer' }}
                              onChange={() => {
                                if (cov.isRequired) return;
                                setFieldValue(`coverages[${index}].checked`, !cov.checked);
                              }}
                            />
                            <span style={{ fontSize: 14, color: cov.checked ? '#111827' : '#9CA3AF', fontWeight: cov.checked ? 600 : 400, lineHeight: 1.2 }}>
                              {cov.name} {cov.isRequired && <span style={{ color: '#EF4444', fontSize: 10, display: 'block', marginTop: '2px', fontWeight: 700 }}>OBRIGATÓRIO</span>}
                            </span>
                          </div>

                          {cov.checked && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '100px', justifyContent: 'flex-end' }}>
                              {!cov.onEdit ? (
                                <>
                                  <strong style={{ fontSize: 15, color: '#111827', whiteSpace: 'nowrap' }}>
                                    {numberToMoneyString(cov.value || 0)}
                                  </strong>
                                  <FaPencilAlt
                                    size={14}
                                    color="#6B7280"
                                    style={{ cursor: 'pointer', padding: '4px', boxSizing: 'content-box' }}
                                    onClick={() => {
                                      setFieldValue(`coverages[${index}].onEdit`, true);
                                      setFieldValue(`coverages[${index}].onEditValue`, cov.value || cov.minValue);
                                    }}
                                  />
                                </>
                              ) : (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <StyledInput
                                    placeholder="R$ 0,00"
                                    style={{ width: '90px', padding: '6px', fontSize: '13px', textAlign: 'right', border: '1px solid #D1D5DB', borderRadius: '4px', margin: 0 }}
                                    value={cov.onEditValue ? numberToMoneyString(cov.onEditValue) : ''}
                                    onChange={(e: any) => {
                                      const onlyNumber = e.target.value.replace(/\D/g, '');
                                      const val = Number(onlyNumber) / 100;
                                      setFieldValue(`coverages[${index}].onEditValue`, val);
                                    }}
                                  />
                                  <FaCheck
                                    size={18}
                                    color="#10B981"
                                    style={{ cursor: 'pointer', padding: '4px', boxSizing: 'content-box', background: '#D1FAE5', borderRadius: '50%' }}
                                    onClick={() => {
                                      const val = cov.onEditValue;
                                      if (val < cov.minValue || val > cov.maxValue) {
                                        warningNotification('Valor inválido', `O valor para ${cov.name} deve ser entre ${numberToMoneyString(cov.minValue)} e ${numberToMoneyString(cov.maxValue)}`);
                                        return;
                                      }
                                      if (cov.maxMultiplier && values.rentValue && val > cov.maxMultiplier * values.rentValue) {
                                        warningNotification('Valor limite!', `O valor inserido excede o limite configurado pelo multiplicador (R$ ${numberToMoneyString(cov.maxMultiplier * values.rentValue)}).`);
                                        return;
                                      }
                                      setFieldValue(`coverages[${index}].value`, val);
                                      setFieldValue(`coverages[${index}].onEdit`, false);
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  <FooterButtons>
                    <BackButton onClick={onBack} disabled={isSubmitting}>Cancelar</BackButton>
                    <SubmitButton type="button" onClick={() => submitForm()} disabled={isSubmitting}>
                      {isSubmitting ? 'Cotando...' : 'Cotar Agora'}
                    </SubmitButton>
                  </FooterButtons>
                </FormSection>
              )}
            </Formik>
          )}

        </ValidationContainer>
      </Content>
    </Container>
  );
};

export default PwaAlfaBudgetForm;
